import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import useUomConvert from 'hooks/useUomConvert';
import { useFormikContext } from 'formik';
import { useLocation } from 'react-router-dom';
import {
  FormValues,
  Product
} from 'screens/Applications&WorkOrders/StepsLayout/Constants/formikData';
import { isEqual } from 'lodash';
import { message } from 'antd';
import INITIAL_CARRIER_PRODUCT from 'screens/Applications&WorkOrders/StepsLayout/Components/ProductsStep/constants/initialCarrierProduct';
import useProductCarrier from 'screens/Applications&WorkOrders/StepsLayout/Components/ProductsStep/hooks/useProductCarrier';
import { useAgOperationUtility } from 'screens/Applications&WorkOrders/StepsLayout/Context/AgOperationUtilityProvider';
import {
  isLoadedFlow,
  RoutePathState
} from 'screens/Applications&WorkOrders/RouteAdmin';
import { calculateTotalCost } from '../../helpers/calculateProduct';
import { QUANTITY_MODES } from '../FillProductDetailsDrawer/constants';
import {
  calculateCarrierTotals,
  calculateTotalProductsByKey,
  differenceBetweenApplications,
  getNonCarrierLiquidProducts,
  recalculateApplicationValues
} from './helpers';
import { useProductStep } from '../../hooks/useProductStep';
import useUpdateWithFeedback from './useUpdateWithFeedback';

const useProductsTable = () => {
  const { values: application, setFieldValue } = useFormikContext<FormValues>();
  const convertFn = useUomConvert();
  const [currentPage, setCurrentPage] = useState(1);
  const [isEditCarrierDrawerOpen, setIsEditCarrierDrawerOpen] = useState(false);
  const { state } = useLocation<RoutePathState | undefined>();

  const { setRecentlyUpdated, update } = useProductStep();
  const { utilityState, updateUtilityState } = useAgOperationUtility();

  const handleMessageClose = (messageKey?: string) => {
    update({ isUpdatingTankInfo: false, field: null });
    if (messageKey) {
      message.destroy(messageKey);
    } else {
      // Fallback to the default key for backward compatibility
      message.destroy('tank-info-updated');
    }
  };

  const { executeUpdateWithFeedback } = useUpdateWithFeedback({
    setRecentlyUpdated,
    update,
    handleMessageClose
  });

  const {
    loadedCarrier,
    isLoading,
    hasSettledWithoutCarrier
  } = useProductCarrier();

  const products = useMemo(() => application.products || [], [
    application.products
  ]);

  const refreshCarrierFromSelection = useCallback(() => {
    if (isEditCarrierDrawerOpen) return;

    if (!utilityState.selectedCarrier || !loadedCarrier || isLoading) {
      return;
    }

    // Skip if loadedCarrier is stale (hasn't caught up with selectedCarrier yet)
    const selectedId = utilityState.selectedCarrier.replaceAll('-', '');
    const loadedCarrierId = loadedCarrier.id?.replaceAll('-', '');
    if (selectedId !== loadedCarrierId) return;

    const currentCarrier = products.find(product => product.isCarrier);
    if (!currentCarrier) {
      return;
    }

    // Detect if the carrier is actually switching to a different product
    const currentCarrierId = currentCarrier.id?.replaceAll('-', '');
    const isSameCarrier = currentCarrierId === loadedCarrierId;

    const refreshedCarrier = {
      ...currentCarrier,
      ...loadedCarrier,
      isCarrier: true,
      isHidden: currentCarrier.isHidden,
      // Mirror the same-carrier price logic used in EditCarrierDrawer.mergedLoadedCarrier():
      // - Same carrier: preserve user's existing price (with fallback to averagePricePerUnit)
      // - Different carrier: use the new carrier's list price (same as createNewCarrier)
      ...(isSameCarrier
        ? {
            priceValue:
              currentCarrier.priceValue ??
              loadedCarrier?.averagePricePerUnit ??
              0,
            priceUnit:
              typeof currentCarrier.priceUnit === 'string' &&
              currentCarrier.priceUnit !== ''
                ? currentCarrier.priceUnit
                : loadedCarrier?.averagePricePerUnitUnit ??
                  currentCarrier.stdunit
          }
        : {
            priceValue:
              loadedCarrier.priceValue ??
              loadedCarrier?.averagePricePerUnit ??
              0,
            priceUnit:
              typeof loadedCarrier.priceUnit === 'string' &&
              loadedCarrier.priceUnit !== ''
                ? loadedCarrier.priceUnit
                : loadedCarrier?.averagePricePerUnitUnit ??
                  loadedCarrier.stdunit
          })
    } as Product;

    if (isEqual(currentCarrier, refreshedCarrier)) {
      return;
    }

    setFieldValue(
      'products',
      products.map(product => (product.isCarrier ? refreshedCarrier : product))
    );
  }, [
    isEditCarrierDrawerOpen,
    utilityState.selectedCarrier,
    loadedCarrier,
    isLoading,
    products,
    setFieldValue
  ]);

  const toggleCarrierRowVisibility = (product: Product) => {
    setFieldValue('isCarrierRowHidden', !product.isHidden);
    setFieldValue('products', [
      ...products.filter(p => !p.isCarrier),
      { ...product, isHidden: !product.isHidden }
    ]);
  };

  const productsToCalculateTotalCost = useMemo(() => {
    if (application.isCarrierRowHidden) {
      return application.products.filter(p => !p.isCarrier);
    }
    return application.products;
  }, [application.products, application.isCarrierRowHidden]);

  const totalCost = calculateTotalCost(productsToCalculateTotalCost) ?? 0;

  useEffect(() => {
    setFieldValue('totalCost', totalCost);
  }, [totalCost, setFieldValue]);

  const deleteProduct = (productIdx: number) => {
    const updatedProducts = application.products?.filter(
      (_, idx) => idx !== productIdx
    );

    setFieldValue('products', updatedProducts);
  };

  const prevProductsRef = useRef<Product[]>([]);

  useEffect(() => {
    const currentCarrier = products?.find(p => p.isCarrier);

    if (
      utilityState.originalCarrierIsHidden === undefined &&
      isLoadedFlow(state?.flow)
    ) {
      updateUtilityState({
        originalCarrierIsHidden:
          currentCarrier?.isHidden ?? !!application?.isTankInfoEnabled
      });
    }
  }, [
    products,
    state?.flow,
    updateUtilityState,
    utilityState.originalCarrierIsHidden,
    application?.isTankInfoEnabled
  ]);

  // Helper function to create a new carrier product
  const createNewCarrier = useCallback(
    ({
      shouldHideCarrierByDefault
    }: {
      shouldHideCarrierByDefault?: boolean;
    }) => {
      const isHidden =
        shouldHideCarrierByDefault ?? application.isCarrierRowHidden;

      if (loadedCarrier) {
        return {
          ...INITIAL_CARRIER_PRODUCT,
          ...loadedCarrier,
          isHidden,
          appliedAreaValue:
            loadedCarrier?.appliedAreaValue ?? application.totalAppliedAcreage,
          applicationMethod:
            loadedCarrier.applicationMethod ??
            application.applicationMethod ??
            '',
          /* If price value is null/undefined it should fallback to loaded carrier average price,
           otherwise use a default price value of 0
        */
          priceValue:
            loadedCarrier.priceValue ?? loadedCarrier?.averagePricePerUnit ?? 0,
          /* If price unit is not a string or is empty it should fallback to loaded carrier average price unit,
           otherwise use stdunit
        */
          priceUnit:
            typeof loadedCarrier.priceUnit === 'string' &&
            loadedCarrier.priceUnit !== ''
              ? loadedCarrier.priceUnit
              : loadedCarrier?.averagePricePerUnitUnit ?? loadedCarrier.stdunit
        };
      }

      return {
        ...INITIAL_CARRIER_PRODUCT,
        appliedAreaValue: application.totalAppliedAcreage,
        isHidden,
        applicationMethod: application.applicationMethod ?? ''
      };
    },
    [
      loadedCarrier,
      application.totalAppliedAcreage,
      application.isCarrierRowHidden,
      application.applicationMethod
    ]
  );

  // Helper function to check if all tank info fields are filled
  const areAllTankInfoFieldsFilled = useCallback(() => {
    const {
      tankSize,
      tankCount,
      overallRatePerAcre,
      totalProduct
    } = application;
    return [tankSize, tankCount, overallRatePerAcre, totalProduct].every(
      tankInfo => typeof tankInfo === 'number'
    );
  }, [application]);

  // Helper function to handle negative total product scenario
  const handleNegativeTotalProduct = useCallback(
    (sumOfTotalProducts: number) => {
      const updatedApplication = recalculateApplicationValues(
        application,
        'totalProduct',
        sumOfTotalProducts,
        convertFn
      );

      const { changedFields } = differenceBetweenApplications(
        application,
        updatedApplication
      );

      executeUpdateWithFeedback({
        recentlyUpdatedData: {
          fields: [...changedFields, 'totalProduct']
        },
        updateData: {
          isUpdatingTankInfo: true,
          field: {
            name: 'totalProduct',
            currentValue: application.totalProduct,
            newValue: sumOfTotalProducts
          }
        },
        messageContent: 'Tank Information updated to reflect saved products.'
      });

      setFieldValue('products', updatedApplication.products);
      setFieldValue('totalProduct', sumOfTotalProducts);
      setFieldValue('tankCount', updatedApplication.tankCount);
      setFieldValue('tankSize', updatedApplication.tankSize);
      setFieldValue(
        'overallRatePerAcre',
        updatedApplication.overallRatePerAcre
      );
    },
    [application, executeUpdateWithFeedback, setFieldValue, convertFn]
  );

  // Helper function to update products with new carrier
  const updateProductsWithCarrier = useCallback(
    (newCarrier: Product) => {
      const newProducts = [...products.filter(p => !p.isCarrier), newCarrier];

      if (isEqual(newProducts, products)) return;

      const nonCarrierProducts = getNonCarrierLiquidProducts(
        application.products
      );
      const sumOfTotalProducts = calculateTotalProductsByKey(
        nonCarrierProducts,
        'totalProduct'
      );

      // Handle negative total product scenario
      if (
        application.totalProduct &&
        application.totalProduct < sumOfTotalProducts
      ) {
        handleNegativeTotalProduct(sumOfTotalProducts);
        return;
      }

      prevProductsRef.current = products;
      setFieldValue('products', newProducts);
    },
    [
      products,
      application.products,
      application.totalProduct,
      setFieldValue,
      handleNegativeTotalProduct
    ]
  );

  // Helper function to handle tank info enabled scenario
  const handleTankInfoEnabled = useCallback(() => {
    const carrier = products.find(p => p.isCarrier);

    if (!carrier) {
      if (
        !isLoading &&
        ((utilityState.selectedCarrier && loadedCarrier) ||
          !utilityState.selectedCarrier)
      ) {
        const isCarrierHidden = isLoadedFlow(state?.flow)
          ? utilityState.originalCarrierIsHidden ??
            !!application.isTankInfoEnabled
          : utilityState.loadedUserPreferences?.isCarrierHidden ?? false;
        setFieldValue('isCarrierRowHidden', isCarrierHidden);
        const newCarrier = createNewCarrier({
          shouldHideCarrierByDefault: isCarrierHidden
        });

        const newProducts = [...products.filter(p => !p.isCarrier), newCarrier];

        prevProductsRef.current = products;
        setFieldValue('products', newProducts);
      }
    } else if (areAllTankInfoFieldsFilled()) {
      // Update existing carrier with tank info
      const {
        overallRatePerAcre,
        tankCount,
        tankSize,
        totalProduct
      } = application;

      const carrierTotals = calculateCarrierTotals(
        products,
        {
          overallRatePerAcre: overallRatePerAcre ?? 0,
          tankCount: tankCount ?? 0,
          tankSize: tankSize ?? 0,
          overallTotalProduct: totalProduct ?? 0
        },
        convertFn
      );

      const newCarrier = {
        ...carrier,
        ...carrierTotals,
        appliedAreaValue: application.totalAppliedAcreage
      };
      updateProductsWithCarrier(newCarrier);
    }
  }, [
    loadedCarrier,
    isLoading,
    utilityState.selectedCarrier,
    utilityState.loadedUserPreferences,
    utilityState.originalCarrierIsHidden,
    products,
    createNewCarrier,
    areAllTankInfoFieldsFilled,
    application,
    setFieldValue,
    updateProductsWithCarrier,
    state?.flow,
    convertFn
  ]);

  // Helper function to handle tank info disabled scenario
  const handleTankInfoDisabled = useCallback(() => {
    const carrier = products.find(p => p.isCarrier);
    if (!carrier) return;

    const newProducts = products
      .filter(p => !p.isCarrier)
      .map(product => {
        // Set the quantity mode to RATE_PER_ACRE if it is RATE_PER_TANK and tank info is disabled
        const quantityMode =
          product.quantityMode === QUANTITY_MODES.RATE_PER_TANK
            ? QUANTITY_MODES.RATE_PER_ACRE
            : product.quantityMode;

        return {
          ...product,
          quantityMode,
          // Set the rate per tank to 0 if tank info is disabled
          ratePerTank: 0
        };
      });
    prevProductsRef.current = products;

    setFieldValue('products', newProducts);
  }, [products, setFieldValue]);

  useEffect(() => {
    if (application.isTankInfoEnabled) {
      handleTankInfoEnabled();
    } else {
      handleTankInfoDisabled();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    application.isTankInfoEnabled,
    utilityState.selectedCarrier,
    isLoading,
    handleTankInfoEnabled,
    handleTankInfoDisabled
  ]);

  useEffect(() => {
    refreshCarrierFromSelection();
  }, [refreshCarrierFromSelection]);

  return {
    totalCost,
    currentPage,
    setCurrentPage,
    toggleCarrierRowVisibility,
    isEditCarrierDrawerOpen,
    setIsEditCarrierDrawerOpen,
    deleteProduct,
    products,
    isTankInfoEnabled: application.isTankInfoEnabled,
    isCarrierRowHidden: application.isCarrierRowHidden || false,
    isLoadingCarrier:
      !!application.isTankInfoEnabled &&
      (isLoading ||
        (!hasSettledWithoutCarrier && !products.some(p => p.isCarrier)))
  };
};
export default useProductsTable;
